import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit, Eye, CheckCircle, XCircle, X } from 'lucide-react';
import { Program, programsApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

const ProgramStatusEnum = ['Active', 'Inactive'] as const;
type ProgramStatus = typeof ProgramStatusEnum[number];


const ProgramManagement: React.FC = () => {
    const { user } = useAuth();

    // Data states
    const [programs, setPrograms] = useState<Program[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Filters & Pagination states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'' | ProgramStatus>('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [totalPages, setTotalPages] = useState(1);

    // Form states for create/edit/view
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [name, setName] = useState('');
    const [preDuration, setPreDuration] = useState(0);
    const [postDuration, setPostDuration] = useState(0);
    const [domainDays, setDomainDays] = useState(0);
    const [OjtDays, setOjtDays] = useState(0);
    const [launchpadDays, setLaunchpadDays] = useState(0);
    const [basecampDays, setBasecampDays] = useState(0);
    const [programCode, setProgramCode] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState<ProgramStatus>('Active');
    const [validationErrors, setValidationErrors] = useState({
        name: '',
        programCode: '',
        preDuration: '',
        postDuration: '',
        domainDays: '',
        OjtDays: '',
        launchpadDays: '',
        basecampDays: '',
    });

    if (user?.role !== 'SYSTEM_ADMIN') {
        return (
            <div className="flex items-center justify-center p-8 text-center text-gray-500">
                You do not have permission to view this module.
            </div>
        );
    }


    // Load programs from API whenever filters or page changes
    useEffect(() => {
        const loadPrograms = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await programsApi.getPrograms({
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm || undefined,
                    queryStatus: statusFilter || undefined,
                });
                if (response.success && response.data) {
                    setPrograms(response.data);
                    if (response.meta) {
                        setTotalPages(response.meta.totalPages);
                    }
                } else {
                    setError(`Failed to fetch programs: ${response.message}`);
                }
            } catch (e) {
                setError(`Failed to fetch programs: ${e}`);
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        loadPrograms();
    }, [searchTerm, statusFilter, currentPage]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Effect to populate form when editing
    useEffect(() => {
        if (showEditModal && selectedProgram) {
            setName(selectedProgram.name);
            setProgramCode(selectedProgram.program_code);
            setDescription(selectedProgram.description || '');
            setPreDuration(selectedProgram.pre_assessment_duration_minutes);
            setPostDuration(selectedProgram.post_assessment_duration_minutes);
            setDomainDays(selectedProgram.domain_training_days);
            setOjtDays(selectedProgram.ojt_days)
            setLaunchpadDays(selectedProgram.launchpad_days);
            setBasecampDays(selectedProgram.basecamp_days);
            setStatus(selectedProgram.status);
        } else {
            resetForm();
        }
    }, [showEditModal, selectedProgram]);

    // Reset form fields
    const resetForm = () => {
        setName('');
        setProgramCode('');
        setDescription('');
        setPreDuration(0);
        setPostDuration(0);
        setDomainDays(0);
        setOjtDays(0);
        setLaunchpadDays(0);
        setBasecampDays(0);
        setStatus('Active');
        setValidationErrors({
            name: '',
            programCode: '',
            preDuration: '',
            postDuration: '',
            domainDays: '',
            OjtDays: '',
            launchpadDays: '',
            basecampDays: '',
        });
    };


    // Handle create submission
    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationErrors({
            name: '',
            programCode: '',
            preDuration: '',
            postDuration: '',
            domainDays: '',
            OjtDays: '',
            launchpadDays: '',
            basecampDays: '',
        });

        const trimmedName = name.trim();
        const trimmedCode = programCode.trim();
        const trimmedDesc = description.trim();

        let hasError = false;
        if (!trimmedName) {
            setValidationErrors((prev) => ({ ...prev, name: 'Program name is required.' }));
            hasError = true;
        }
        if (!trimmedCode) {
            setValidationErrors((prev) => ({ ...prev, programCode: 'Program code is required.' }));
            hasError = true;
        }
        if (!preDuration) {
            setValidationErrors((prev) => ({ ...prev, preDuration: 'Pre assessment duration is required.' }));
            hasError = true;
        }
        if (!postDuration) {
            setValidationErrors((prev) => ({ ...prev, postDuration: 'Post assessment duration is required.' }));
            hasError = true;
        }
        if (!domainDays) {
            setValidationErrors((prev) => ({ ...prev, domainDays: 'Number of domain training days is required.' }));
            hasError = true;
        }
        if (!OjtDays) {
            setValidationErrors((prev) => ({ ...prev, OjtDays: 'Number of OJT days is required.' }));
            hasError = true;
        }
        if (!launchpadDays) {
            setValidationErrors((prev) => ({ ...prev, launchpadDays: 'Number of launchpad days is required.' }));
            hasError = true;
        }
        if (!basecampDays) {
            setValidationErrors((prev) => ({ ...prev, basecampDays: 'Number of base camp days is required.' }));
            hasError = true;
        }

        if (hasError) return;

        setError(null);
        setSubmitLoading(true);
        try {
            const response = await programsApi.createProgram({
                name: trimmedName,
                program_code: trimmedCode,
                description: trimmedDesc,
                pre_assessment_duration_minutes: preDuration,
                post_assessment_duration_minutes: postDuration,
                domain_training_days: domainDays,
                ojt_days: OjtDays,
                launchpad_days: launchpadDays,
                basecamp_days: basecampDays,
            });
            if (response.success && response.data) {
                const newProgram = response.data;
                setSuccess('Program created successfully.');
                setPrograms((prev) => [...prev, newProgram]);
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.message || 'Failed to create program.');
            }
            setShowCreateModal(false);
            resetForm();
        } catch (e) {
            setError('Failed to create program.');
            console.error(e);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Handle edit submission
    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedProgram) return;

        setError(null);
        setValidationErrors({
            name: '',
            programCode: '',
            preDuration: '',
            postDuration: '',
            domainDays: '',
            OjtDays: '',
            launchpadDays: '',
            basecampDays: '',
        });

        const trimmedName = name.trim();
        const trimmedCode = programCode.trim();
        const trimmedDesc = description.trim();

        let hasError = false;
        if (!trimmedName) {
            setValidationErrors((prev) => ({ ...prev, name: 'Program name is required.' }));
            hasError = true;
        }
        if (!trimmedCode) {
            setValidationErrors((prev) => ({ ...prev, programCode: 'Program code is required.' }));
            hasError = true;
        }
         if (!preDuration) {
            setValidationErrors((prev) => ({ ...prev, preDuration: 'Pre assessment duration is required.' }));
            hasError = true;
        }
        if (!postDuration) {
            setValidationErrors((prev) => ({ ...prev, postDuration: 'Post assessment duration is required.' }));
            hasError = true;
        }
        if (!domainDays) {
            setValidationErrors((prev) => ({ ...prev, domainDays: 'Number of domain training days is required.' }));
            hasError = true;
        }
        if (!OjtDays) {
            setValidationErrors((prev) => ({ ...prev, OjtDays: 'Number of OJT days is required.' }));
            hasError = true;
        }
        if (!launchpadDays) {
            setValidationErrors((prev) => ({ ...prev, launchpadDays: 'Number of launchpad days is required.' }));
            hasError = true;
        }
        if (!basecampDays) {
            setValidationErrors((prev) => ({ ...prev, basecampDays: 'Number of base camp days is required.' }));
            hasError = true;
        }

        if (hasError) return;

        setSubmitLoading(true);
        setError(null);
        try {
            const response = await programsApi.updateProgram(String(selectedProgram.program_id), {
                name: trimmedName,
                program_code: trimmedCode,
                description: trimmedDesc,
                pre_assessment_duration_minutes: preDuration,
                post_assessment_duration_minutes: postDuration,
                domain_training_days: domainDays,
                ojt_days: OjtDays,
                launchpad_days: launchpadDays,
                basecamp_days: basecampDays,
                status
            });
            if (response.success && response.data) {
                setSuccess('Program updated successfully.');
                const updatedProg = response.data;
                setPrograms((prev) => prev.map(p => p.program_id === updatedProg.program_id ? updatedProg : p));
                setSelectedProgram(updatedProg);
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.message || 'Failed to update program.');
            }
            setShowEditModal(false);
        } catch (e) {
            setError('Failed to update program.');
            console.error(e);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Status badge helper
    const getStatusBadge = (s: ProgramStatus) => {
        const color = s === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
        const icon = s === 'Active' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />;
        return (
            <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-semibold ${color}`}>
                {icon}
                {s}
            </span>
        );
    };



    const isModalOpen = showCreateModal || showEditModal || showViewModal;

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-6">
            {/* Notifications */}
            {error && !isModalOpen && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}


            {/* Header and Create Button */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Program Management</h1>
                    <p className="mt-2 text-sm text-gray-700">Manage training programs.</p>
                </div>
                <button onClick={() => { setError(null); resetForm(); setShowCreateModal(true); }} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2">
                    <PlusCircle className="h-4 w-4" /> Create New
                </button>
            </div>

               {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg shadow-inner">
                <input
                    type="text"
                    placeholder="Search programs..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full sm:w-1/2 lg:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as '' | ProgramStatus)}
                    className="w-full sm:w-1/4 lg:w-1/6 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">All Statuses</option>
                    {ProgramStatusEnum.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            {/* Program Table */}
            <div className="rounded-xl border bg-white shadow-sm">
                {loading ? (
                    <div className="text-center text-gray-500 py-8">Loading programs...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                             <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Program Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {programs.map(program => (
                                    <tr key={program.program_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">{program.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{program.program_code}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(program.status)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center justify-end gap-2">
                                            <button onClick={() => { setSelectedProgram(program); setShowViewModal(true); }} title="View">
                                                <Eye className="w-5 h-5 text-primary-600 hover:text-primary-900" />
                                            </button>
                                            <button onClick={() => { setError(null); setSelectedProgram(program); setShowEditModal(true); }} title="Edit">
                                                <Edit className="w-5 h-5 text-blue-600 hover:text-blue-900" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination Controls */}
                <div className="px-6 py-4 flex items-center justify-between">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >Previous</button>
                    <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >Next</button>
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-md mx-auto my-6 bg-white rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center pb-4">
                            <h3 className="text-lg font-semibold">Create Program</h3>
                            <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">&times;</button>
                        </div>
                        {error && showCreateModal && (
                            <div className="mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                        <form
                            onSubmit={handleCreateSubmit}
                            onChangeCapture={() => setError(null)}
                            className="space-y-4"
                        >
                            <div className="grid gap-2">
                                <label htmlFor="create-name" className="text-sm font-medium">Program Name</label>
                                <input
                                    id="create-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, name: '' }));
                                    }}
                                    placeholder="e.g., Full Stack Development"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.name && <p className="text-sm text-red-500">{validationErrors.name}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Program Code</label>
                                <input
                                    id="create-code"
                                    type="text"
                                    value={programCode}
                                    onChange={(e) => {
                                        setProgramCode(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, programCode: '' }));
                                    }}
                                    placeholder="e.g., FSD-101"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.programCode && <p className="text-sm text-red-500">{validationErrors.programCode}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-description" className="text-sm font-medium">Description</label>
                                <input
                                    id="create-description"
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                             <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Pre Assessment Total Duration (in minutes)</label>
                                <input
                                    id="create-code"
                                    type="number"
                                    value={preDuration}
                                    onChange={(e) => {
                                        setPreDuration(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, preDuration: '' }));
                                    }}
                                    placeholder="e.g., 60"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.preDuration && <p className="text-sm text-red-500">{validationErrors.preDuration}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Post Assessment Total Duration (in minutes)</label>
                                <input
                                    id="create-code"
                                    type="number"
                                    value={postDuration}
                                    onChange={(e) => {
                                        setPostDuration(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, postDuration: '' }));
                                    }}
                                    placeholder="e.g., 60"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.postDuration && <p className="text-sm text-red-500">{validationErrors.postDuration}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-launchpad-days" className="text-sm font-medium">Number of days in Launchpad</label>
                                <input
                                    id="create-launchpad-days"
                                    type="number"
                                    value={launchpadDays}
                                    onChange={(e) => {
                                        setLaunchpadDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, launchpadDays: '' }));
                                    }}
                                    placeholder="e.g., 4"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.launchpadDays && <p className="text-sm text-red-500">{validationErrors.launchpadDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-basecamp-days" className="text-sm font-medium">Number of days in Base Camp</label>
                                <input
                                    id="create-basecamp-days"
                                    type="number"
                                    value={basecampDays}
                                    onChange={(e) => {
                                        setBasecampDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, basecampDays: '' }));
                                    }}
                                    placeholder="e.g., 2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.basecampDays && <p className="text-sm text-red-500">{validationErrors.basecampDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Number of days for Domain Training</label>
                                <input
                                    id="create-code"
                                    type="number"
                                    value={domainDays}
                                    onChange={(e) => {
                                        setDomainDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, domainDays: '' }));
                                    }}
                                    placeholder="e.g., 60"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.domainDays && <p className="text-sm text-red-500">{validationErrors.domainDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Number of days for OJT</label>
                                <input
                                    id="create-code"
                                    type="number"
                                    value={OjtDays}
                                    onChange={(e) => {
                                        setOjtDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, OjtDays: '' }));
                                    }}
                                    placeholder="e.g., 60"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.OjtDays && <p className="text-sm text-red-500">{validationErrors.OjtDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-status" className="text-sm font-medium">Status</label>
                                <select
                                    id="create-status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as ProgramStatus)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-gray-200 bg-white shadow-sm hover:bg-gray-100 h-9 px-4 py-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-gray-900 text-gray-50 shadow hover:bg-gray-900/90 h-9 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showCreateModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showCreateModal ? 'Creating...' : 'Create Program'}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedProgram && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-md mx-auto my-6 bg-white rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center pb-4">
                            <h3 className="text-lg font-semibold">Edit Program</h3>
                            <button onClick={() => { setShowEditModal(false); setSelectedProgram(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">&times;</button>
                        </div>
                        {error && showEditModal && (
                            <div className="mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                        <form
                            onSubmit={handleEditSubmit}
                            onChangeCapture={() => setError(null)}
                            className="space-y-4"
                        >
                            <div className="grid gap-2">
                                <label htmlFor="edit-name" className="text-sm font-medium">Program Name</label>
                                <input
                                    id="edit-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, name: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.name && <p className="text-sm text-red-500">{validationErrors.name}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-code" className="text-sm font-medium">Program Code</label>
                                <input
                                    id="edit-code"
                                    type="text"
                                    value={programCode}
                                    onChange={(e) => {
                                        setProgramCode(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, programCode: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.programCode && <p className="text-sm text-red-500">{validationErrors.programCode}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-description" className="text-sm font-medium">Description</label>
                                <input
                                    id="edit-description"
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-code" className="text-sm font-medium">Pre Assesment Toal Duration (in minutes)</label>
                                <input
                                    id="edit-code"
                                    type="number"
                                    value={preDuration}
                                    onChange={(e) => {
                                        setPreDuration(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, preDuration: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.preDuration && <p className="text-sm text-red-500">{validationErrors.preDuration}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-code" className="text-sm font-medium">Post Assesment Toal Duration (in minutes)</label>
                                <input
                                    id="edit-code"
                                    type="number"
                                    value={postDuration}
                                    onChange={(e) => {
                                        setPostDuration(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, postDuration: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.postDuration && <p className="text-sm text-red-500">{validationErrors.postDuration}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-launchpad-days" className="text-sm font-medium">Number of days in Launchpad</label>
                                <input
                                    id="edit-launchpad-days"
                                    type="number"
                                    value={launchpadDays}
                                    onChange={(e) => {
                                        setLaunchpadDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, launchpadDays: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.launchpadDays && <p className="text-sm text-red-500">{validationErrors.launchpadDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-basecamp-days" className="text-sm font-medium">Number of days in Base Camp</label>
                                <input
                                    id="edit-basecamp-days"
                                    type="number"
                                    value={basecampDays}
                                    onChange={(e) => {
                                        setBasecampDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, basecampDays: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.basecampDays && <p className="text-sm text-red-500">{validationErrors.basecampDays}</p>}
                            </div>
                             <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Number of days for Domain Training</label>
                                <input
                                    id="edit-code"
                                    type="number"
                                    value={domainDays}
                                    onChange={(e) => {
                                        setDomainDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, domainDays: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.domainDays && <p className="text-sm text-red-500">{validationErrors.domainDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="create-code" className="text-sm font-medium">Number of days for OJT</label>
                                <input
                                    id="edit-code"
                                    type="number"
                                    value={OjtDays}
                                    onChange={(e) => {
                                        setOjtDays(Number(e.target.value));
                                        setValidationErrors((prev) => ({ ...prev, OjtDays: '' }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {validationErrors.OjtDays && <p className="text-sm text-red-500">{validationErrors.OjtDays}</p>}
                            </div>
                            <div className="grid gap-2">
                                <label htmlFor="edit-status" className="text-sm font-medium">Status</label>
                                <select
                                    id="edit-status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as ProgramStatus)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => { setShowEditModal(false); setSelectedProgram(null); resetForm(); }}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-gray-200 bg-white shadow-sm hover:bg-gray-100 h-9 px-4 py-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-gray-900 text-gray-50 shadow hover:bg-gray-900/90 h-9 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showEditModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showEditModal ? 'Saving...' : 'Save Changes'}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* View Modal */}
            {showViewModal && selectedProgram && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center">
                        <div className="relative w-full max-w-md mx-auto my-6 bg-white rounded-lg shadow-lg p-6">
                        <div className="flex justify-between items-center pb-4">
                            <h3 className="text-lg font-semibold">View Program Details</h3>
                            <button onClick={() => { setShowViewModal(false); setSelectedProgram(null); }} className="text-gray-400 hover:text-gray-600">&times;</button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <h3 className="text-xl font-bold">{selectedProgram.name}</h3>
                                {getStatusBadge(selectedProgram.status)}
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Program Code</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.program_code}</p>
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Description</p>
                                <p className="text-gray-900">{selectedProgram.description || 'N/A'}</p>
                            </div>
                              <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Pre Assessment total duration (in minutes)</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.pre_assessment_duration_minutes}</p>
                            </div>
                             <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Post Assessment total duration (in minutes)</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.post_assessment_duration_minutes}</p>
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Number of days in Launchpad</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.launchpad_days}</p>
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Number of days in Base Camp</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.basecamp_days}</p>
                            </div>
                             <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Number of days for Domain Training</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.domain_training_days}</p>
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Number of days for OJT</p>
                                <p className="text-gray-900 font-medium">{selectedProgram.ojt_days}</p>
                            </div>
                            <div className="grid gap-2">
                                <p className="text-sm text-gray-500">Created At</p>
                                <p className="text-gray-900">{new Date(selectedProgram.created_at).toLocaleString()}</p>
                            </div>
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => { setShowViewModal(false); setSelectedProgram(null); }}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-gray-900 text-gray-50 shadow hover:bg-gray-900/90 h-9 px-4 py-2"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
}


export default ProgramManagement;
